import type { QualifyingStatusEnum } from '@iheartjane/types';
import { formatCurrency, pluralize } from '@iheartjane/utils';

import { getSpecialQualifyingMetrics } from './storeSpecialDetailUtils';
import type {
  QualifyingStatusProduct,
  SpecialConditions,
  SpecialRules,
} from './storeSpecialDetailUtils.types';

interface StatusTextSpecialBase {
  discount_dollar_amount?: number | null;
  discount_percent?: number | null;
  discount_type?: string;
  title?: string;
}

type BundleStatusTextSpecial = StatusTextSpecialBase & {
  conditions?: {
    bundle?: NonNullable<SpecialConditions>['bundle'];
  } | null;
  special_type: 'bundle';
};

type BulkPricingStatusTextSpecial = StatusTextSpecialBase & {
  conditions?: {
    bulk_pricing?: NonNullable<SpecialConditions>['bulk_pricing'];
  } | null;
  special_type: 'bulk_pricing';
};

type RulesStatusTextSpecial = StatusTextSpecialBase & {
  rules?: SpecialRules;
  special_type: 'cart_total' | 'product' | 'spending_threshold';
};

type QualifiedGroupStatusTextSpecial = StatusTextSpecialBase & {
  special_type: 'qualified_group';
};

export type StatusTextSpecial =
  | BundleStatusTextSpecial
  | BulkPricingStatusTextSpecial
  | RulesStatusTextSpecial
  | QualifiedGroupStatusTextSpecial;

export interface StatusTextQualifyingStatus {
  discount_total_amount?: number;
  discounted_products?: QualifyingStatusProduct[];
  product_price?: number;
  product_weight?: number;
  qualified_products?: QualifyingStatusProduct[];
  qualifying_status?: QualifyingStatusEnum | string | null;
  target_weight?: number;
}

interface GetStatusTextArgs {
  currentSpecial?: StatusTextSpecial;
  qualifyingStatus?: StatusTextQualifyingStatus | null;
}

function getCalloutText(
  remainingQualifiers: number,
  remainingDiscountables: number,
  isUnlocked: boolean,
  qualifyBy: 'dollar_amount' | 'product_count' | 'weight',
  requiredQualifierCount?: number | null
) {
  let qualifyByText = '';

  switch (qualifyBy) {
    case 'dollar_amount':
      qualifyByText = `Spend ${formatCurrency(remainingQualifiers, true)}`;
      break;
    case 'product_count':
      qualifyByText = `Add ${pluralize({
        noun: 'product',
        number: remainingQualifiers,
        returnValue: 'both',
      })}`;
      break;
    case 'weight':
      qualifyByText = `Add ${parseFloat(remainingQualifiers.toFixed(1))}g`;
      break;
    default:
      qualifyByText = `Add ${pluralize({
        noun: 'product',
        number: remainingQualifiers,
        returnValue: 'both',
      })}`;
  }

  if (!isUnlocked) {
    if (qualifyBy === 'weight' && remainingQualifiers < 0) {
      return `Remove ${parseFloat(
        Math.abs(remainingQualifiers).toFixed(1)
      )}g to unlock deal`;
    }
    if (remainingQualifiers <= 0) {
      return requiredQualifierCount === 1
        ? 'Try a different item to unlock deal'
        : 'Try different items to unlock deal';
    }
    return `${qualifyByText} to unlock deal`;
  }

  return remainingDiscountables > 0
    ? `Add up to ${remainingDiscountables} more ${pluralize({
        noun: 'product',
        number: remainingDiscountables,
        returnValue: 'word',
      })}`
    : '';
}

function getCalloutSubtext(
  currentSpecial: StatusTextSpecial,
  remainingQualifiers: number,
  remainingDiscountables: number,
  isUnlocked: boolean,
  qualifyBy: 'dollar_amount' | 'product_count' | 'weight',
  targetWeight?: number
) {
  if (
    qualifyBy === 'weight' &&
    !isUnlocked &&
    remainingQualifiers < 0 &&
    targetWeight
  ) {
    return `Total must equal ${targetWeight}g exactly`;
  }

  let cashBackPer = '';

  switch (currentSpecial.discount_type) {
    case 'dollar_amount':
      cashBackPer = formatCurrency(currentSpecial.discount_dollar_amount, true);
      break;
    case 'percent':
      cashBackPer = `${currentSpecial.discount_percent}%`;
      break;
    case 'target_price':
      cashBackPer = '';
      break;
    default:
      cashBackPer = '';
  }

  if (!cashBackPer) return '';

  return remainingQualifiers <= 0 && remainingDiscountables > 0
    ? `${cashBackPer} off each`
    : '';
}

export const getStatusText = ({
  qualifyingStatus,
  currentSpecial,
}: GetStatusTextArgs) => {
  if (!(qualifyingStatus && currentSpecial))
    return {
      calloutText: '',
      calloutSubtext: '',
      footerText: '',
      footerSubtext: '',
      stickyText: '',
      stickyCallout: '',
      stickyCalloutSubtext: '',
    };

  const isUnlocked =
    qualifyingStatus.qualifying_status === 'applied' ||
    qualifyingStatus.qualifying_status === 'qualified';

  const isBulkType = currentSpecial.special_type === 'bulk_pricing';
  const isCartTotalType = currentSpecial.special_type === 'cart_total';
  const isProductType = currentSpecial.special_type === 'product';
  const isSpendingThresholdType =
    currentSpecial.special_type === 'spending_threshold';

  const qualifyBy = isBulkType
    ? 'weight'
    : isSpendingThresholdType || isCartTotalType
      ? 'dollar_amount'
      : 'product_count';

  const {
    remainingQualifiers,
    remainingDiscountables,
    reachedMaxDiscountables,
  } = getSpecialQualifyingMetrics({ currentSpecial, qualifyingStatus });

  const requiredQualifierCount =
    currentSpecial.special_type === 'bundle'
      ? currentSpecial.conditions?.bundle?.independent
          ?.threshold_number_of_items_in_cart
      : undefined;

  const calloutText = !isProductType
    ? getCalloutText(
        remainingQualifiers,
        remainingDiscountables,
        isUnlocked,
        qualifyBy,
        requiredQualifierCount
      )
    : '';

  const calloutSubtext = getCalloutSubtext(
    currentSpecial,
    remainingQualifiers,
    remainingDiscountables,
    isUnlocked,
    qualifyBy,
    qualifyingStatus.target_weight
  );

  const formattedTotalAmount = formatCurrency(
    qualifyingStatus.discount_total_amount,
    true
  );
  const totalCashBack = `${formattedTotalAmount} saved with this deal`;

  const showMaxedOut = reachedMaxDiscountables && isUnlocked;

  const footerText =
    isProductType || !isUnlocked
      ? ''
      : showMaxedOut
        ? 'You maxed out your discount!'
        : qualifyingStatus.discount_total_amount &&
            qualifyingStatus.discount_total_amount > 0
          ? `${totalCashBack}!`
          : 'Deal unlocked!';

  const footerSubtext = showMaxedOut ? totalCashBack : '';

  const stickyText = showMaxedOut
    ? 'You maxed out your discount!'
    : !isProductType && isUnlocked
      ? 'Deal unlocked!'
      : currentSpecial.title || '';

  const stickyCallout = isProductType
    ? ''
    : showMaxedOut
      ? totalCashBack
      : calloutText;

  return {
    calloutText,
    calloutSubtext,
    footerText,
    footerSubtext,
    stickyText,
    stickyCallout,
    stickyCalloutSubtext: calloutSubtext && `(${calloutSubtext})`,
  };
};
